import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { GenericForm, FormField } from '../../../components/GenericForm';
import { toast } from 'react-toastify';
import { LeadDistributorModel } from '../models/LeadDistributorModel';
import {
  retrieveLeadDistributor,
  updateLeadDistributor,
} from '../services/LeadDistributorCRUD';

const LOADING_STATUS = 'LOADING_STATUS';
const IDLE_STATUS = 'IDLE_STATUS';

export default function LeadDistributorDetail({ id }) {
  const intl = useIntl();
  const [status, setStatus] = useState(IDLE_STATUS);
  const [error, setError]: any | undefined = useState({});
  const [editing, setEditing] = useState(false);
  const [distributor, setDistributor] = useState<LeadDistributorModel | null>(
    null
  );

  const fetchDistributor = async (id) => {
    try {
      const { data } = await retrieveLeadDistributor(id);
      setDistributor(data);
    } catch (error) {
      console.warn(error);
    }
  };

  useEffect(() => {
    fetchDistributor(id);
  }, [id]);

  async function onSubmit(formState: LeadDistributorModel) {
    try {
      setStatus(LOADING_STATUS);
      const { data } = await updateLeadDistributor(formState);
      setDistributor(data);
      setEditing(false);
      toast.success(intl.formatMessage({ id: 'distributorDetail.saved' }));
    } catch (error) {
      console.warn(error);
      if (error.response?.data) {
        setError(error.response?.data);
      }
      toast.error(intl.formatMessage({ id: 'distributorDetail.errorSaving' }));
    } finally {
      setStatus(IDLE_STATUS);
    }
  }

  const formFields: Array<FormField<LeadDistributorModel>> = [
    {
      id: 'name',
      label: intl.formatMessage({ id: 'distributors.name' }),
      inputType: 'text',
    },
    {
      id: 'email',
      label: intl.formatMessage({ id: 'distributors.email' }),
      inputType: 'email',
    },
    {
      id: 'phone',
      label: intl.formatMessage({ id: 'distributors.phone' }),
      inputType: 'text',
      maxLength: 14,
    },
    {
      id: 'pending_review',
      label: intl.formatMessage({ id: 'distributors.pendingReview' }),
      inputType: 'checkbox',
    },
    {
      id: 'created_at',
      label: intl.formatMessage({ id: 'distributors.createdAt' }),
      inputType: 'date',
      readonly: true,
    },
    {
      id: 'contact_person',
      label: intl.formatMessage({ id: 'leadDistributors.contactPerson' }),
      inputType: 'text',
      maxLength: 150,
    },
    {
      id: 'pos_name',
      label: intl.formatMessage({ id: 'leadDistributors.posName' }),
      inputType: 'text',
      maxLength: 200,
    },
    {
      id: 'business_type',
      label: intl.formatMessage({ id: 'leadDistributors.businessType' }),
      inputType: 'select',
      options: [
        {
          value: 'pet-store',
          label: intl.formatMessage({
            id: 'leadDistributors.businessType.petStore',
          }),
        },
        {
          value: 'vet-clinic',
          label: intl.formatMessage({
            id: 'leadDistributors.businessType.vetClinic',
          }),
        },
        {
          value: 'grooming',
          label: intl.formatMessage({
            id: 'leadDistributors.businessType.grooming',
          }),
        },
        {
          value: 'daycare',
          label: intl.formatMessage({
            id: 'leadDistributors.businessType.daycare',
          }),
        },
        {
          value: 'others',
          label: intl.formatMessage({
            id: 'leadDistributors.businessType.others',
          }),
        },
      ],
    },
    {
      id: 'region',
      label: intl.formatMessage({ id: 'leadDistributors.region' }),
      inputType: 'select',
      options: [
        { value: 'andalucia', label: 'Andaluc\u00eda' },
        { value: 'aragon', label: 'Arag\u00f3n' },
        { value: 'asturias', label: 'Asturias' },
        { value: 'baleares', label: 'Islas Baleares' },
        { value: 'canarias', label: 'Canarias' },
        { value: 'cantabria', label: 'Cantabria' },
        { value: 'castilla-la-mancha', label: 'Castilla-La Mancha' },
        { value: 'castilla-y-leon', label: 'Castilla y Le\u00f3n' },
        { value: 'cataluna', label: 'Catalu\u00f1a' },
        { value: 'ceuta', label: 'Ceuta' },
        { value: 'comunidad-valenciana', label: 'Comunidad Valenciana' },
        { value: 'extremadura', label: 'Extremadura' },
        { value: 'galicia', label: 'Galicia' },
        { value: 'la-rioja', label: 'La Rioja' },
        { value: 'madrid', label: 'Madrid' },
        { value: 'melilla', label: 'Melilla' },
        { value: 'murcia', label: 'Murcia' },
        { value: 'navarra', label: 'Navarra' },
        { value: 'pais-vasco', label: 'Pa\u00eds Vasco' },
      ],
    },
    {
      id: 'street',
      label: intl.formatMessage({ id: 'leadDistributors.street' }),
      inputType: 'text',
      maxLength: 150,
    },
    {
      id: 'postcode',
      label: intl.formatMessage({ id: 'leadDistributors.postcode' }),
      inputType: 'text',
      maxLength: 20,
    },
    {
      id: 'city',
      label: intl.formatMessage({ id: 'leadDistributors.city' }),
      inputType: 'text',
      maxLength: 150,
    },
    {
      id: 'discovery_source',
      label: intl.formatMessage({ id: 'leadDistributors.discoverySource' }),
      inputType: 'text',
      maxLength: 250,
    },
    {
      id: 'inquiry_details',
      label: intl.formatMessage({ id: 'leadDistributors.inquiryDetails' }),
      inputType: 'textarea',
    },
  ];

  return (
    <div className="card p-10">
      {status === 'loading_request_user' ? (
        <div className="spinner-border text-primary" role="status" />
      ) : (
        <>
          {distributor ? (
            <>
              <GenericForm
                columns={2}
                error={error}
                editing={editing}
                toggleEdit={() => setEditing(!editing)}
                title={intl.formatMessage({
                  id: 'leadDistributorDetail.infoTitle',
                })}
                initialState={distributor}
                fields={formFields}
                ctaLabel={intl.formatMessage({
                  id: 'distributorDetail.saveChanges',
                })}
                onSubmit={onSubmit}
                submitting={status === LOADING_STATUS}
                submittingLabel={intl.formatMessage({
                  id: 'distributorDetail.pleaseWait',
                })}
              />
            </>
          ) : (
            <p>
              {intl.formatMessage({ id: 'distributorDetail.noDistributor' })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
